import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MOCK_CASE_NUMBERS } from "@/components/case_portal/mockCases";
import { useCasePortal } from "@/context/CasePortalContext";
import { useToast } from "@/hooks/useToast";
import { Check } from "lucide-react";

const ORDER_OPTIONS = [
  {
    id: "pay_only_probation_order",
    label: "Pay Only Probation Order",
  },
  {
    id: "order_of_probation",
    label: "Order of Probation",
  },
] as const;

type OrderOption = (typeof ORDER_OPTIONS)[number];
type OrderId = OrderOption["id"];

const normalizeCaseNumber = (value: string) => value.replace(/\s+/g, "").toUpperCase();

const pdfUrlFor = ({ caseNumber, orderId }: { caseNumber: string; orderId: OrderId }) => {
  // This entire Case Portal flow is mocked; keep it straightforward and explicit.
  if (normalizeCaseNumber(caseNumber) === "26M104381") {
    return orderId === "pay_only_probation_order"
      ? "/case-portal/26M104381%20-%20Pay%20Only%20Probation%20Order.pdf"
      : "/case-portal/26M104381%20-%20Order%20of%20Probation.pdf";
  }

  // Default to the first mock case for any other case number.
  return orderId === "pay_only_probation_order"
    ? "/case-portal/26M004381%20-%20Pay%20Only%20Probation%20Order.pdf"
    : "/case-portal/26M004381%20-%20Order%20of%20Probation.pdf";
};

export function CasePortal() {
  const { openCaseRequest, setActiveCaseNumber } = useCasePortal();
  const { toast } = useToast();
  const [caseNumberInput, setCaseNumberInput] = useState("");
  const [isCaseInputFocused, setIsCaseInputFocused] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(true);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderOption | null>(null);
  const [submittedCaseNumber, setSubmittedCaseNumber] = useState("");
  const [signatureSent, setSignatureSent] = useState(false);

  const normalizedCaseNumber = useMemo(
    () => normalizeCaseNumber(caseNumberInput),
    [caseNumberInput]
  );
  const hasCaseNumber = normalizedCaseNumber.length > 0;

  const matchingCaseNumbers = useMemo(() => {
    if (!isCaseInputFocused) return [];
    if (normalizedCaseNumber.length === 0) return [];
    return MOCK_CASE_NUMBERS.filter(
      (caseNumber) =>
        caseNumber.startsWith(normalizedCaseNumber) && caseNumber !== normalizedCaseNumber
    );
  }, [isCaseInputFocused, normalizedCaseNumber]);

  const showSuggestion = matchingCaseNumbers.length > 0;

  useEffect(() => {
    if (!openCaseRequest) return;

    const normalized = normalizeCaseNumber(openCaseRequest.caseNumber);
    setCaseNumberInput(normalized);
    setSubmittedCaseNumber(normalized);
    setSelectedOrder(null);
    setIsCaseInputFocused(false);

    // Behave like the user already searched successfully.
    setSearchDialogOpen(false);
    setOrderDialogOpen(true);
    setActiveCaseNumber(normalized);
  }, [openCaseRequest, setActiveCaseNumber]);

  useEffect(() => {
    // Reset when the user changes case or order.
    setSignatureSent(false);
  }, [submittedCaseNumber, selectedOrder?.id]);

  const handleCaseSubmit = () => {
    if (!hasCaseNumber) {
      return;
    }

    setSubmittedCaseNumber(normalizedCaseNumber);
    setSelectedOrder(null);
    setSearchDialogOpen(false);
    setOrderDialogOpen(true);
    setActiveCaseNumber(normalizedCaseNumber);
  };

  const handleSendForSignature = () => {
    if (!selectedOrder) return;
    if (signatureSent) return;

    setSignatureSent(true);
    toast({
      title: "Sent for signature",
      description: `${selectedOrder.label} has been sent for signature.`,
    });
  };

  const handleSelectSuggestion = (caseNumber: string) => {
    setCaseNumberInput(caseNumber);
    setIsCaseInputFocused(false);
  };

  const handleSelectOrder = (order: OrderOption) => {
    setOrderDialogOpen(false);
    setSelectedOrder(order);
  };

  return (
    <div className="flex-1 overflow-auto bg-muted/20 p-6">
      <Dialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Select a Case</DialogTitle>
            <DialogDescription>
              Enter a case number to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative space-y-2">
              <Label htmlFor="case-portal-case-number">Case Number</Label>
              <Input
                id="case-portal-case-number"
                value={caseNumberInput}
                onChange={(event) => setCaseNumberInput(event.target.value.toUpperCase())}
                onFocus={() => setIsCaseInputFocused(true)}
                onBlur={() => {
                  window.setTimeout(() => setIsCaseInputFocused(false), 100);
                }}
                placeholder="Start typing case number"
                autoComplete="off"
              />

              {showSuggestion && (
                <div className="absolute left-0 top-full z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
                  <Command shouldFilter={false}>
                    <CommandList>
                      <CommandGroup>
                        {matchingCaseNumbers.map((caseNumber) => (
                          <CommandItem
                            key={caseNumber}
                            value={caseNumber}
                            onSelect={() => handleSelectSuggestion(caseNumber)}
                          >
                            {caseNumber}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>

            {hasCaseNumber && (
              <Button onClick={handleCaseSubmit}>
                Search Case
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Select an Order</DialogTitle>
            <DialogDescription>
              Choose which order to view for case {submittedCaseNumber}.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            {ORDER_OPTIONS.map((order) => (
              <Button
                key={order.id}
                type="button"
                variant="outline"
                className="h-auto justify-start py-4 text-left"
                onClick={() => handleSelectOrder(order)}
              >
                {order.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {selectedOrder && (
        <div className="mx-auto max-w-6xl">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Case <span className="font-semibold text-foreground">{submittedCaseNumber}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                onClick={handleSendForSignature}
                disabled={signatureSent}
              >
                {signatureSent && <Check className="mr-2 h-4 w-4" />}
                {signatureSent ? "Sent for Signature" : "Send for Signature"}
              </Button>
              <Button variant="outline" onClick={() => setSearchDialogOpen(true)}>
                Search Another Case
              </Button>
              <Button variant="outline" onClick={() => setOrderDialogOpen(true)}>
                Change Order
              </Button>
            </div>
          </div>
          <Card>
            <CardHeader>
              <div className="space-y-1.5">
                <CardTitle>{selectedOrder.label}</CardTitle>
                <CardDescription>
                  Mock PDF preview for case {submittedCaseNumber}.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="h-[75vh]">
              <div className="h-full overflow-hidden rounded-md border">
                <iframe
                  title={`${selectedOrder.label} PDF`}
                  src={pdfUrlFor({ caseNumber: submittedCaseNumber, orderId: selectedOrder.id })}
                  className="h-full w-full"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!selectedOrder && !searchDialogOpen && (
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center rounded-lg border border-dashed bg-background/80 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No order selected yet.
          </p>
          <Button className="mt-4" onClick={() => setSearchDialogOpen(true)}>
            Search Case
          </Button>
        </div>
      )}
    </div>
  );
}
